import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clinician, ProfessionalType, TestResult } from '../types';
import { updateClinician } from '../services/clinicianService';
import { getUserProfile, updateProfessionalType } from '../services/profileService';
import { useConsent, CONSENT_LABELS } from '../hooks/useConsent';
import type { ConsentType } from '../hooks/useConsent';
import { useFormValidation } from '../hooks/useFormValidation';
import ExportMenu from './ExportMenu';

type TabId = 'perfil' | 'consentimientos' | 'datos';

interface ProfilePageProps {
  clinician: Clinician;
  results: TestResult[];
  onUpdate: (updatedClinician: Clinician) => void;
}

export default function ProfilePage({ clinician, results, onUpdate }: ProfilePageProps) {
  const { t } = useTranslation(['app', 'common']);
  const [activeTab, setActiveTab] = useState<TabId>('perfil');
  const [formData, setFormData] = useState({
    name: clinician.name,
    email: clinician.email,
    phone: clinician.phone || '',
    licenseNumber: clinician.licenseNumber || '',
    specialization: clinician.specialization || '',
  });

  const [professionalType, setProfessionalType] = useState<ProfessionalType>('psychologist');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [consentActionLoading, setConsentActionLoading] = useState<ConsentType | null>(null);

  const { consents, loading: consentsLoading, giveConsent, removeConsent, refreshConsents } = useConsent();
  const { errors, validateField, validateAll, clearErrors } = useFormValidation<{
    name: string;
    email: string;
  }>({
    name: (value) => {
      if (!value.trim()) return t('app:profile.nameRequired');
      return null;
    },
    email: (value) => {
      if (!value.trim()) return t('app:profile.emailRequired');
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return t('app:profile.emailInvalid');
      return null;
    },
  });

  useEffect(() => {
    setFormData({
      name: clinician.name,
      email: clinician.email,
      phone: clinician.phone || '',
      licenseNumber: clinician.licenseNumber || '',
      specialization: clinician.specialization || '',
    });
    setError(null);
    setSuccess(false);
  }, [clinician]);

  useEffect(() => {
    loadProfessionalType();
  }, []);

  const loadProfessionalType = async () => {
    setIsLoadingProfile(true);
    try {
      const profile = await getUserProfile();
      if (profile) {
        setProfessionalType(profile.professionalType);
      } else {
        console.warn('No profile found, using default: psychologist');
        setProfessionalType('psychologist');
      }
    } catch (err) {
      console.error('Error loading professional type:', err);
      setProfessionalType('psychologist');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextValues = { ...formData, [name]: value };
    setFormData(nextValues);
    if (errors[name as 'name' | 'email']) {
      validateField(name as 'name' | 'email', value, nextValues);
    }
    setError(null);
    setSuccess(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateAll({ name: formData.name, email: formData.email });
    if (!isValid) {
      setError(t('app:profile.reviewFields'));
      return;
    }

    setIsSaving(true);
    setError(null);
    clearErrors();

    try {
      const updatedClinician = await updateClinician(clinician.id, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || undefined,
        licenseNumber: formData.licenseNumber.trim() || undefined,
        specialization: formData.specialization.trim() || undefined,
      });

      if (!updatedClinician) {
        throw new Error(t('app:profile.updateFailed'));
      }

      const profileUpdated = await updateProfessionalType(professionalType);
      if (!profileUpdated) {
        console.warn('Could not update professional type, but clinician data was saved');
      }

      setSuccess(true);
      onUpdate(updatedClinician);
    } catch (err) {
      console.error('Error updating clinician profile:', err);
      setError(t('app:profile.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'perfil', label: t('app:profile.tabs.profile') },
    { id: 'consentimientos', label: t('app:profile.tabs.consents') },
    { id: 'datos', label: t('app:profile.tabs.data') },
  ];

  return (
    <div className="bg-white shadow rounded-lg p-3 lg:p-6 space-y-4 lg:space-y-6">
      <div className="flex flex-col gap-3 lg:gap-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base lg:text-2xl font-bold text-slate-800">
              {t('app:profile.title')}
            </h2>
            <p className="text-xs lg:text-sm text-slate-500 mt-1">
              {t('app:profile.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-xs lg:text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-green-800 font-medium">{t('app:profile.updateSuccess')}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert" id="profile-error">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {activeTab === 'perfil' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('app:profile.fullName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={isSaving}
                      aria-invalid={!!errors.name || !!error}
                      aria-describedby={errors.name ? 'profile-name-error' : error ? 'profile-error' : undefined}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder={t('app:profile.namePlaceholder')}
                      required
                    />
                    {errors.name && (
                      <p id="profile-name-error" className="mt-1 text-sm text-red-600" role="alert">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('app:profile.emailLabel')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={isSaving}
                      aria-invalid={!!errors.email || !!error}
                      aria-describedby={errors.email ? 'profile-email-error' : error ? 'profile-error' : undefined}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder={t('app:profile.emailPlaceholder')}
                      required
                    />
                    {errors.email && (
                      <p id="profile-email-error" className="mt-1 text-sm text-red-600" role="alert">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="specialization" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('app:profile.specializationOptional')}
                    </label>
                    <input
                      type="text"
                      id="specialization"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleInputChange}
                      disabled={isSaving}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder={t('app:profile.specializationPlaceholder')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('app:profile.specializationHint')}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('app:profile.licenseLabel')}
                    </label>
                    <input
                      type="text"
                      id="licenseNumber"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleInputChange}
                      disabled={isSaving}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder={t('app:profile.licensePlaceholder')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('app:profile.licenseHint')}
                    </p>
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('app:profile.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={isSaving}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder={t('app:profile.phonePlaceholder')}
                  />
                </div>

                <div className="border border-slate-200 rounded-lg p-4">
                  <label htmlFor="professionalType" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('app:profile.professionalTypeLabel')} <span className="text-red-500">*</span>
                  </label>
                  {isLoadingProfile ? (
                    <div className="flex items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-gray-200">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-sm text-gray-600">{t('app:profile.loadingConfig')}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                        professionalType === 'psychologist'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="professionalType"
                          value="psychologist"
                          checked={professionalType === 'psychologist'}
                          onChange={(e) => setProfessionalType(e.target.value as ProfessionalType)}
                          disabled={isSaving}
                          className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {t('app:profile.psychologist')}
                            {professionalType === 'psychologist' && (
                              <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                {t('app:profile.current')}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {t('app:profile.psychologistDesc')}
                          </div>
                        </div>
                      </label>
                      <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                        professionalType === 'psychiatrist'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="professionalType"
                          value="psychiatrist"
                          checked={professionalType === 'psychiatrist'}
                          onChange={(e) => setProfessionalType(e.target.value as ProfessionalType)}
                          disabled={isSaving}
                          className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {t('app:profile.psychiatrist')}
                            {professionalType === 'psychiatrist' && (
                              <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                {t('app:profile.current')}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {t('app:profile.psychiatristDesc')}
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                  <p className="text-xs text-blue-600 mt-2 flex items-start gap-1">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{t('app:profile.professionalTypeHint')}</span>
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'consentimientos' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900">{t('app:profile.consentsTitle')}</h3>
                </div>
                <p className="text-sm text-gray-600">
                  {t('app:profile.consentsGdpr')}
                </p>

                {consentsLoading ? (
                  <div className="flex items-center justify-center p-6 bg-gray-50 rounded-lg">
                    <svg className="animate-spin h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-sm text-gray-600">{t('app:profile.loadingConsents')}</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{CONSENT_LABELS.terms}</span>
                        <span className="ml-2 text-xs text-gray-500">({t('app:profile.required')})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {consents?.terms ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {t('app:profile.accepted')}
                          </span>
                        ) : (
                          <span className="text-xs text-red-600">{t('app:profile.notRegistered')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{CONSENT_LABELS.privacy}</span>
                        <span className="ml-2 text-xs text-gray-500">({t('app:profile.required')})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {consents?.privacy ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {t('app:profile.accepted')}
                          </span>
                        ) : (
                          <span className="text-xs text-red-600">{t('app:profile.notRegistered')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{CONSENT_LABELS.ai_analysis}</span>
                        <p className="text-xs text-blue-600 mt-0.5">{t('app:profile.includedInPrivacy')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {consents?.ai_analysis ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {t('app:profile.active')}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600">{t('app:profile.pending')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{CONSENT_LABELS.third_party}</span>
                        <p className="text-xs text-blue-600 mt-0.5">{t('app:profile.includedInTerms')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {consents?.third_party ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {t('app:profile.active')}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600">{t('app:profile.pending')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{CONSENT_LABELS.marketing}</span>
                        <span className="ml-2 text-xs text-gray-500">({t('app:profile.optional')})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {consents?.marketing ? (
                          <>
                            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              {t('app:profile.active')}
                            </span>
                            <button
                              type="button"
                              onClick={async () => {
                                setConsentActionLoading('marketing');
                                await removeConsent('marketing', 'Revocado por el usuario desde perfil');
                                await refreshConsents();
                                setConsentActionLoading(null);
                              }}
                              disabled={consentActionLoading === 'marketing'}
                              className="text-xs text-red-600 hover:text-red-800 hover:underline disabled:opacity-50"
                            >
                              {consentActionLoading === 'marketing' ? t('app:profile.revoking') : t('app:profile.revoke')}
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-gray-500">{t('app:profile.inactive')}</span>
                            <button
                              type="button"
                              onClick={async () => {
                                setConsentActionLoading('marketing');
                                await giveConsent('marketing');
                                await refreshConsents();
                                setConsentActionLoading(null);
                              }}
                              disabled={consentActionLoading === 'marketing'}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                            >
                              {consentActionLoading === 'marketing' ? t('app:profile.activating') : t('app:profile.activate')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-4 flex items-start gap-1.5">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    {t('app:profile.consentsFooter')}
                  </span>
                </p>
              </div>
            )}

            {activeTab === 'datos' && (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('app:profile.dataExportTitle')}</h3>
                    <p className="text-sm text-gray-600">
                      {t('app:profile.dataExportDesc')}
                    </p>
                  </div>
                  <ExportMenu results={results} disabled={isSaving} />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-700">
                  {t('app:profile.dataExportNote')}
                </div>
              </div>
            )}

            {activeTab === 'perfil' && (
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="submit"
                  disabled={isSaving || success}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('app:profile.saving')}
                    </>
                  ) : success ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('app:profile.saved')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('app:profile.saveChanges')}
                    </>
                  )}
                </button>
              </div>
            )}
      </form>
    </div>
  );
}
